import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';

/**
 * How the live stage is dressed, for everybody.
 *
 * ## Why it lives on the features page
 *
 * It is the same kind of act as flipping a flag: one choice, made by an
 * operator, that every reader sees at once with no release and no diff. Its own
 * page would be a page somebody visits twice a year, and putting it beside the
 * flags means the screen answers one question - "what is this platform doing
 * right now" - instead of two.
 *
 * ## Radio buttons, not a toggle per season
 *
 * A stage wears one thing. Modelled as flags it would be possible to have
 * Halloween and Christmas on at once, and the drawing would have to decide
 * which wins - a question nobody should have to answer, and one the server
 * makes unaskable by storing a single value.
 *
 * ## Nothing is scheduled
 *
 * The season note beside each theme is a reminder to whoever is choosing, not a
 * timer. A stage that redecorated itself at midnight during somebody's live
 * broadcast would be a surprise nobody asked for, and the person who would have
 * to explain it is the host.
 */
interface Theme {
  key: string;
  label: string;
  description: string;
  /** Roughly when it belongs, or null for the everyday one. */
  season: string | null;
  current: boolean;
}

export function StageThemePicker() {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['admin-stage-theme'],
    queryFn: () => api.get<{ themes: Theme[] }>('/admin/stage-theme'),
  });

  const choose = useMutation({
    mutationFn: (theme: string) => api.post<{ themes: Theme[] }>('/admin/stage-theme', { theme }),
    onSuccess: (result) => {
      /*
       * Written straight back rather than refetched. The server answers with
       * the whole list in its new state, so a second request would ask it to
       * repeat itself and leave a flicker in between.
       */
      queryClient.setQueryData(['admin-stage-theme'], result);
    },
  });

  if (!data) return null;

  return (
    <Card className="flex flex-col gap-4 p-5" data-stage-theme>
      <div className="flex flex-col gap-1">
        <h2 className="text-[15px] font-semibold tracking-tight text-ink">Stage theme</h2>
        <p className="text-sm leading-relaxed text-soft">
          What the drawn stage is decorated with, in every live room at once. Listeners see it the
          next time a room loads; nothing about a broadcast in progress is interrupted.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {data.themes.map((theme) => (
          <label
            key={theme.key}
            data-theme-option={theme.key}
            className={`flex cursor-pointer items-start gap-3 rounded-[var(--radius-control)] border p-3.5 transition-colors ${
              theme.current
                ? 'border-accent bg-raised'
                : 'border-line hover:border-line-strong hover:bg-raised'
            }`}
          >
            <input
              type="radio"
              name="stage-theme"
              value={theme.key}
              checked={theme.current}
              disabled={choose.isPending}
              onChange={() => choose.mutate(theme.key)}
              className="mt-1 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
            />

            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="flex flex-wrap items-baseline gap-2">
                <span className="text-sm font-medium text-ink">{theme.label}</span>
                {theme.season !== null && (
                  <span className="font-mono text-[10px] text-faint">{theme.season}</span>
                )}
              </span>
              <span className="text-[13px] leading-relaxed text-soft">{theme.description}</span>
            </span>
          </label>
        ))}
      </div>

      {choose.isError && (
        <p className="text-[13px] text-negative">That did not save. Try again.</p>
      )}

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        Recorded in the audit log, including a press that changes nothing.
      </p>
    </Card>
  );
}
